import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { FolderOpen, Upload, Download, Trash2, FileText, User, Search } from 'lucide-react';
import api from '../lib/api';
import { FilePreview } from '../components/FilePreview';
import { useAuth } from '../contexts/AuthContext';

const DOC_TYPES = [
  { value: 'busta_paga', label: 'Busta Paga' },
  { value: 'attestato_corso', label: 'Attestato Corso' },
  { value: 'certificazione', label: 'Certificazione' },
  { value: 'contratto', label: 'Contratto' },
  { value: 'documento_identita', label: 'Documento Identita' },
  { value: 'altro', label: 'Altro' }
];

export default function PersonalDocsPage() {
  const { user, isRole } = useAuth();
  const [docs, setDocs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({ doc_type: 'busta_paga', title: '', description: '', file: null });
  const [uploading, setUploading] = useState(false);
  
  // Admin mode: upload for other users
  const [showAdminUpload, setShowAdminUpload] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [adminUploadForm, setAdminUploadForm] = useState({ doc_type: 'busta_paga', title: '', description: '', file: null });
  const [viewingUserId, setViewingUserId] = useState('');
  const [viewingUserDocs, setViewingUserDocs] = useState([]);

  const load = async () => {
    try {
      const docsRes = await api.get('/personal-docs/my');
      setDocs(docsRes.data);
      
      if (isRole('admin', 'aspp', 'qhse')) {
        const usersRes = await api.get('/users');
        setUsers(usersRes.data.filter(u => u.is_active));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.title) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      
      await api.post(`/personal-docs/upload?doc_type=${uploadForm.doc_type}&title=${encodeURIComponent(uploadForm.title)}&description=${encodeURIComponent(uploadForm.description || '')}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setShowUpload(false);
      setUploadForm({ doc_type: 'busta_paga', title: '', description: '', file: null });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore durante il caricamento');
    } finally {
      setUploading(false);
    }
  };

  const handleAdminUpload = async () => {
    if (!adminUploadForm.file || !adminUploadForm.title || !selectedUserId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', adminUploadForm.file);
      
      await api.post(`/personal-docs/upload-for-user/${selectedUserId}?doc_type=${adminUploadForm.doc_type}&title=${encodeURIComponent(adminUploadForm.title)}&description=${encodeURIComponent(adminUploadForm.description || '')}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setShowAdminUpload(false);
      setAdminUploadForm({ doc_type: 'busta_paga', title: '', description: '', file: null });
      setSelectedUserId('');
      
      // Refresh if viewing that user
      if (viewingUserId === selectedUserId) {
        const res = await api.get(`/personal-docs/user/${viewingUserId}`);
        setViewingUserDocs(res.data);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore durante il caricamento');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try {
      await api.delete(`/personal-docs/${docId}`);
      load();
      if (viewingUserId) {
        const res = await api.get(`/personal-docs/user/${viewingUserId}`);
        setViewingUserDocs(res.data);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const viewUserDocs = async (userId) => {
    try {
      const res = await api.get(`/personal-docs/user/${userId}`);
      setViewingUserDocs(res.data);
      setViewingUserId(userId);
    } catch (err) {
      alert('Errore nel caricamento documenti');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getDocTypeLabel = (type) => DOC_TYPES.find(t => t.value === type)?.label || type;

  const filteredDocs = docs.filter(d => 
    d.title?.toLowerCase().includes(search.toLowerCase()) ||
    d.doc_type?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-slate-400">Caricamento...</div></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="personal-docs-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">I Miei Documenti</h1>
        <Button onClick={() => setShowUpload(true)} data-testid="upload-doc-btn">
          <Upload size={16} className="mr-1" /> Carica Documento
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
        <Input
          placeholder="Cerca documenti..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* My Documents */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <FolderOpen size={18} className="text-blue-600" />
            Documenti Personali
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">Qui trovi i tuoi documenti caricati e quelli inseriti dalla azienda nella tua scheda dipendente.</p>
        </CardHeader>
        <CardContent>
          {filteredDocs.length === 0 ? (
            <p className="text-center text-slate-400 py-8">Nessun documento. Clicca "Carica Documento" per aggiungere.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Documento</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="hidden sm:table-cell">Fonte</TableHead>
                  <TableHead className="hidden sm:table-cell">Data</TableHead>
                  <TableHead className="hidden sm:table-cell">Stato</TableHead>
                  <TableHead className="w-24">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDocs.map(doc => (
                  <TableRow key={doc.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText size={16} className="text-slate-400" />
                        <div>
                          <p className="font-medium text-sm">{doc.title}</p>
                          {doc.description && <p className="text-xs text-slate-500">{doc.description}</p>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{getDocTypeLabel(doc.doc_type)}</Badge>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <Badge variant="outline" className={doc.source === 'employee' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-blue-50 text-blue-700 border-blue-200'}>
                        {doc.source === 'employee' ? 'Scheda Personale' : 'Caricato'}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-slate-500">
                      {doc.created_at ? new Date(doc.created_at).toLocaleDateString('it-IT') : '-'}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {doc.source === 'employee' && doc.status && (
                        <Badge variant="outline" className={
                          doc.status === 'scaduto' ? 'bg-red-50 text-red-700 border-red-200' :
                          doc.status === 'in_scadenza' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          'bg-green-50 text-green-700 border-green-200'
                        }>
                          {doc.status === 'scaduto' ? 'Scaduto' : doc.status === 'in_scadenza' ? 'In Scadenza' : 'Valido'}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {doc.file_url && (
                          <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="sm" className="p-1 h-auto text-blue-600">
                              <Download size={14} />
                            </Button>
                          </a>
                        )}
                        {doc.source === 'personal' && (
                          <Button variant="ghost" size="sm" className="p-1 h-auto text-red-600" onClick={() => handleDelete(doc.id)}>
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Admin Section: View/Upload for other users */}
      {isRole('admin', 'aspp', 'qhse') && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <User size={18} className="text-purple-600" />
                Gestione Documenti Dipendenti
              </CardTitle>
              <Button variant="outline" size="sm" onClick={() => setShowAdminUpload(true)} data-testid="admin-upload-btn">
                <Upload size={14} className="mr-1" /> Carica per Dipendente
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <Select value={viewingUserId} onValueChange={viewUserDocs}>
                <SelectTrigger className="w-64" data-testid="select-user-view">
                  <SelectValue placeholder="Seleziona dipendente per visualizzare..." />
                </SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {viewingUserId && (
              <div className="border rounded-lg p-4 bg-slate-50">
                <p className="text-sm font-medium mb-3">Documenti di: {users.find(u => u.id === viewingUserId)?.email}</p>
                {viewingUserDocs.length === 0 ? (
                  <p className="text-sm text-slate-500">Nessun documento per questo utente.</p>
                ) : (
                  <div className="space-y-2">
                    {viewingUserDocs.map(doc => (
                      <div key={doc.id} className="flex items-center justify-between p-2 bg-white rounded border">
                        <div className="flex items-center gap-2">
                          <FileText size={14} className="text-slate-400" />
                          <span className="text-sm">{doc.title}</span>
                          <Badge variant="outline" className="text-xs">{getDocTypeLabel(doc.doc_type)}</Badge>
                        </div>
                        <div className="flex gap-1">
                          <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="sm" className="p-1 h-auto"><Download size={12} /></Button>
                          </a>
                          <Button variant="ghost" size="sm" className="p-1 h-auto text-red-600" onClick={() => handleDelete(doc.id)}>
                            <Trash2 size={12} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upload Dialog (own documents) */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Carica Documento</DialogTitle>
            <DialogDescription>Carica un documento nella tua cartella personale</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Tipo Documento *</Label>
              <Select value={uploadForm.doc_type} onValueChange={(v) => setUploadForm({ ...uploadForm, doc_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Titolo *</Label>
              <Input value={uploadForm.title} onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })} placeholder="Es. Busta Paga Marzo 2026" />
            </div>
            <div className="space-y-1.5">
              <Label>Descrizione</Label>
              <Input value={uploadForm.description} onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} placeholder="Opzionale" />
            </div>
            <div className="space-y-1.5">
              <Label>File *</Label>
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })} />
              <p className="text-xs text-slate-500">Formati: PDF, DOC, DOCX, JPG, PNG</p>
            </div>
            <FilePreview file={uploadForm.file} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Annulla</Button>
            <Button onClick={handleUpload} disabled={!uploadForm.file || !uploadForm.title || uploading}>
              {uploading ? 'Caricamento...' : 'Carica'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin Upload Dialog */}
      <Dialog open={showAdminUpload} onOpenChange={setShowAdminUpload}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Carica Documento per Dipendente</DialogTitle>
            <DialogDescription>Carica buste paga, attestati o altri documenti per un dipendente</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Dipendente *</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger data-testid="admin-select-user"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Tipo Documento *</Label>
              <Select value={adminUploadForm.doc_type} onValueChange={(v) => setAdminUploadForm({ ...adminUploadForm, doc_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Titolo *</Label>
              <Input value={adminUploadForm.title} onChange={(e) => setAdminUploadForm({ ...adminUploadForm, title: e.target.value })} placeholder="Es. Busta Paga Marzo 2026" />
            </div>
            <div className="space-y-1.5">
              <Label>Descrizione</Label>
              <Input value={adminUploadForm.description} onChange={(e) => setAdminUploadForm({ ...adminUploadForm, description: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>File *</Label>
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => setAdminUploadForm({ ...adminUploadForm, file: e.target.files?.[0] || null })} />
            </div>
            <FilePreview file={adminUploadForm.file} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdminUpload(false)}>Annulla</Button>
            <Button onClick={handleAdminUpload} disabled={!adminUploadForm.file || !adminUploadForm.title || !selectedUserId || uploading}>
              {uploading ? 'Caricamento...' : 'Carica'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
